import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, firstValueFrom, take } from 'rxjs';

import { selectParticipantId, selectSessionId } from '../../features/session/store/selectors/session.selectors';
import { CheckInFlowStore } from '../../features/check-in/store/check-in-flow.store';

export const checkInResumeGuard: CanActivateFn = async (route, state) => {
  const platformId = inject(PLATFORM_ID);
  const store = inject(Store);
  const router = inject(Router);
  const checkInFlowStore = inject(CheckInFlowStore);

  if (!isPlatformBrowser(platformId)) {
    return true;
  }

  try {
    await firstValueFrom(
      store.select(selectParticipantId).pipe(
        filter((participantId) => participantId !== null),
        take(1),
      ),
    );

    await firstValueFrom(
      store.select(selectSessionId).pipe(
        filter((sessionId) => !!sessionId),
        take(1),
      ),
    );

    await checkInFlowStore.loadStepStates();

    const firstIncompleteRoute = checkInFlowStore.getFirstIncompleteStepRoute();
    const areAllStepsComplete =
      !firstIncompleteRoute &&
      checkInFlowStore.currentParticipantSteps().length > 0;

    const targetPath = state.url.split('?')[0];

    if (areAllStepsComplete) {
      // This will no longer make sense in a multi-participant flow; will need to
      // redirect somewhere more useful like a waiting room or participant selection page
      return router.createUrlTree(['/signing-room'], {
        queryParams: route.queryParams,
      });
    }

    if (firstIncompleteRoute && firstIncompleteRoute !== targetPath) {
      return router.createUrlTree([firstIncompleteRoute], {
        queryParams: route.queryParams,
      });
    }

    return true;
  } catch {
    return true;
  }
};
